<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';
import SidebarUnreadBadge from './SidebarUnreadBadge.vue';
import SidebarPinButton from './SidebarPinButton.vue';

const props = defineProps({
  name: { type: String, default: '' },
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  tag: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  badgeCount: { type: Number, default: 0 },
  getterKeys: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const count = computed(() =>
  dynamicCount.value > 99 ? '99+' : dynamicCount.value
);
// Só itens de primeiro nível com página própria (sem filhos) podem ser fixados
const canPin = computed(() => !!props.to && !props.expandable);
</script>

<template>
  <div class="relative min-w-0 group/pin">
    <!-- O botão vem antes do link para o `peer-*` do link enxergá-lo -->
    <SidebarPinButton v-if="canPin" :pin-key="name" />
    <component
      :is="to ? 'router-link' : 'div'"
      class="flex items-center gap-2 px-1.5 py-1 rounded-xl h-8 min-w-0"
      role="button"
      draggable="false"
      :to="to"
      :title="label"
      :class="{
        'group-hover/pin:pe-7 peer-aria-pressed:pe-7': canPin,
        'text-n-slate-12 bg-n-solid-blue ring-1 ring-inset ring-n-blue-border font-medium':
          isActive && !hasActiveChild,
        'text-n-slate-12 font-medium': hasActiveChild,
        'text-n-slate-11 hover:bg-n-alpha-2': !isActive && !hasActiveChild,
      }"
      @click.stop="emit('toggle')"
    >
      <div v-if="icon" class="relative flex items-center gap-2">
        <Icon v-if="icon" :icon="icon" class="size-4" />
        <span
          v-if="showBadge"
          class="size-2 -top-px ltr:-right-px rtl:-left-px bg-n-brand absolute rounded-full border border-n-solid-2"
        />
      </div>
      <div class="flex items-center gap-1.5 flex-grow min-w-0 flex-1">
        <span
          class="truncate"
          :class="{
            'text-body-main': !isActive,
            'font-medium text-sm': isActive || hasActiveChild,
          }"
        >
          {{ label }}
        </span>
        <span
          v-if="tag"
          class="rounded-md text-[10px] leading-4 font-medium px-1 flex-shrink-0 bg-n-brand/10 text-n-blue-text"
        >
          {{ tag }}
        </span>
        <span
          v-if="dynamicCount && !expandable"
          class="min-w-5 h-5 grid place-items-center rounded-full bg-n-ruby-9 px-1.5 text-xs leading-none font-bold text-white shadow-md flex-shrink-0"
        >
          {{ count }}
        </span>
        <SidebarUnreadBadge :count="badgeCount" />
      </div>
      <span
        v-if="expandable"
        class="size-3"
        :class="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        @click.stop="emit('toggle')"
      />
    </component>
  </div>
</template>
